import React from "react";
import { format } from "date-fns";
import { fr, es } from "date-fns/locale";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * BUG-193 — the ONE availability-calendar wiring, shared by every surface that
 * lets a client pick a date before its times: public/Reserver.jsx (the original
 * booking flow, which already had this) and public/ManageBooking.jsx's
 * reschedule step (which used to flatten every future day's times
 * into one continuous scroll with no date-selection step at all).
 *
 * `Calendar` — the shadcn wrapper around react-day-picker (components/ui/
 * calendar.jsx) — is passed IN as a prop rather than imported here. That is not
 * indirection for its own sake: Jest has no "@" alias (see craco.config.js), so
 * a component that imports "@/components/ui/calendar" only resolves in a test
 * that either mocks that exact specifier or never renders this tree at all.
 * Reserver.jsx's test suites already mock "@/components/ui/calendar" (a
 * cheap stub, since none of them are testing the calendar itself), and they do
 * so by mocking the STRING Reserver.jsx imports — so Reserver.jsx keeps
 * importing it that way, unchanged, and hands the (possibly stubbed) component
 * down here. ManageBooking.rescheduleCalendar.test.js is the opposite case: it
 * deliberately mocks nothing calendar-related, because it wants the REAL
 * react-day-picker grid to render, so ManageBooking.jsx reaches the same
 * calendar.jsx file through a plain relative import instead. Injecting the
 * component as a prop is what lets both callers keep their own working import
 * style while sharing every actual DECISION below in one place.
 */

// date-fns locale for react-day-picker's own month/weekday names. No entry for
// "en" on purpose: react-day-picker's own default locale is English, and BASE_LANG
// (i18n/index.js) is English too, so an unrecognised `lang` degrades to that same
// default instead of silently reading as French.
const RDP_LOCALES = { fr, es };

export const DAY_KEY_FMT = "yyyy-MM-dd";

// react-day-picker v9 classNames — the shadcn wrapper (components/ui/calendar.jsx)
// still ships v8-era keys, so the correct v9 slot names (UI/DayFlag/SelectionState
// from react-day-picker/dist/esm/UI.js) are supplied here and shallow-merged on
// top of it by the wrapper's own `...classNames` spread.
const CALENDAR_CLASSNAMES = {
  // `relative` on `months` is required: react-day-picker renders `nav` as a
  // sibling of `month` (both children of `months`), and `nav` is
  // `position: absolute` — without a positioned ancestor here it escapes all
  // the way up to the viewport as its containing block (see the library's own
  // stylesheet, which puts `position: relative` on `.rdp-root` for the same
  // reason; we don't import that stylesheet, so it has to be reproduced here).
  months: "flex flex-col relative",
  month: "space-y-3 w-full",
  month_caption: "flex justify-center items-center h-9 mb-1",
  caption_label: "text-sm font-medium text-eh-ink capitalize",
  nav: "flex items-center justify-between absolute inset-x-0 top-0 h-9",
  button_previous: "h-7 w-7 flex items-center justify-center rounded-full text-eh-muted hover:bg-eh-cream hover:text-eh-ink aria-disabled:opacity-30 aria-disabled:pointer-events-none transition",
  button_next: "h-7 w-7 flex items-center justify-center rounded-full text-eh-muted hover:bg-eh-cream hover:text-eh-ink aria-disabled:opacity-30 aria-disabled:pointer-events-none transition",
  month_grid: "w-full border-collapse",
  weekdays: "flex",
  weekday: "text-eh-muted font-normal text-xs w-9 h-8 flex items-center justify-center capitalize",
  week: "flex w-full mt-0.5",
  day: "relative p-0 text-center w-9 h-9 flex items-center justify-center text-sm",
  day_button: "h-8 w-8 rounded-full flex items-center justify-center font-normal text-eh-ink hover:bg-eh-cream transition",
  selected: "[&>button]:bg-eh-ink [&>button]:text-eh-cream [&>button]:hover:bg-eh-ink [&>button]:font-medium",
  today: "[&>button]:ring-1 [&>button]:ring-eh-sage-deep",
  outside: "text-eh-muted/40",
  disabled: "text-eh-muted/30 pointer-events-none",
  hidden: "invisible",
};

// The little dot under a day that has open times — the same affordance on
// both surfaces, so a client sees the same "this day has something" signal on
// the reschedule step as on the original booking calendar.
const AVAILABLE_DAY_CLASSNAME =
  "font-semibold text-eh-ink after:content-[''] after:absolute after:bottom-0.5 after:left-1/2 after:-translate-x-1/2 after:w-1 after:h-1 after:rounded-full after:bg-eh-ochre-deep";

// `components` is passed as ONE object to react-day-picker (below), which
// replaces — never merges with — the shadcn wrapper's own default (IconLeft/
// IconRight, components/ui/calendar.jsx), so both are supplied here together.
const dayPickerComponents = {
  IconLeft: ({ className, ...props }) => <ChevronLeft className={className} {...props} />,
  IconRight: ({ className, ...props }) => <ChevronRight className={className} {...props} />,
};

/**
 * @param {object} props
 * @param {React.ComponentType} props.Calendar    the shadcn wrapper, injected by the caller (see above)
 * @param {Set<string>} props.availableDays        `DAY_KEY_FMT`-formatted keys that have at least one offered start
 * @param {string} props.lang                       current UI language, for react-day-picker's own locale
 * @param {Date|undefined} props.selectedDate        the currently chosen day, or undefined
 * @param {(dayKey: string) => void} props.onSelectDay  fired ONLY for a day already present in `availableDays`
 * @param {Date} props.month                        the month react-day-picker is currently showing
 * @param {(month: Date) => void} props.onMonthChange
 * @param {Date} props.startMonth
 * @param {Date} props.endMonth
 */
export default function AvailabilityCalendar({
  Calendar,
  availableDays,
  lang,
  selectedDate,
  onSelectDay,
  month,
  onMonthChange,
  startMonth,
  endMonth,
}) {
  const isDayDisabled = (date) => !availableDays.has(format(date, DAY_KEY_FMT));
  const dayModifiers = { available: (date) => availableDays.has(format(date, DAY_KEY_FMT)) };
  const dayModifiersClassNames = { available: AVAILABLE_DAY_CLASSNAME };

  const handleSelect = (date) => {
    if (!date) return;
    const key = format(date, DAY_KEY_FMT);
    if (!availableDays.has(key)) return;
    onSelectDay(key);
  };

  return (
    <Calendar
      mode="single"
      selected={selectedDate}
      onSelect={handleSelect}
      disabled={isDayDisabled}
      modifiers={dayModifiers}
      modifiersClassNames={dayModifiersClassNames}
      components={dayPickerComponents}
      locale={RDP_LOCALES[lang]}
      weekStartsOn={1}
      month={month}
      onMonthChange={onMonthChange}
      startMonth={startMonth}
      endMonth={endMonth}
      showOutsideDays={false}
      className="p-0"
      classNames={CALENDAR_CLASSNAMES}
    />
  );
}
